import '../styles/ResultsExportControls.css';
import React, { useEffect, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const FALLBACK_RUN_ID = 'run-2026-02-11-01';

function selectedRunId() {
  if (typeof window === 'undefined') return FALLBACK_RUN_ID;
  const id = new URLSearchParams(window.location.search).get('id');
  return id || FALLBACK_RUN_ID;
}

function stateLabel(state) {
  if (state === 'complete') return 'Complete';
  if (state === 'failed') return 'Failed';
  if (state === 'running') return 'Running';
  return 'State unavailable';
}

function stamp(value) {
  if (!value) return '';
  return String(value).replace('T', ' ').replace('Z', ' UTC');
}

function reportFileName(run) {
  const slug = run && run.id ? run.id : 'results';
  return `brute-report-${slug}.pdf`;
}

function logFileName(run) {
  const slug = run && run.id ? run.id : 'results';
  return `brute-logs-${slug}.txt`;
}

export default function ResultsExportControls({ onExport } = {}) {
  const runs = useProjectCollection('test_run');
  const findings = useProjectCollection('result_finding');

  const [runId, setRunId] = useState(selectedRunId);
  const [destination, setDestination] = useState('');
  const [exportStatus, setExportStatus] = useState('idle');
  const [exportKind, setExportKind] = useState(null);
  const [errorReason, setErrorReason] = useState('');
  const [lastWrittenFile, setLastWrittenFile] = useState('');
  const [notice, setNotice] = useState('');
  const destinationRef = useRef(null);

  useEffect(() => {
    const onPop = () => setRunId(selectedRunId());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const runList = Array.isArray(runs.data) ? runs.data : [];
  const run = runList.find((r) => r.id === runId) || null;
  const runFindings = (Array.isArray(findings.data) ? findings.data : []).filter((f) => f.runId === runId);

  const total = runFindings.length;
  const fails = runFindings.filter((f) => f.outcome === 'fail').length;
  const passes = runFindings.filter((f) => f.outcome === 'pass').length;
  const lastTimestamp = runFindings.reduce((acc, f) => (f.timestamp && f.timestamp > acc ? f.timestamp : acc), '');

  const loading = runs.loading || findings.loading;
  const loadError = runs.error || findings.error;
  const busy = exportStatus === 'busy';

  const startExport = async (kind, targetDestination) => {
    if (typeof onExport !== 'function') {
      setNotice('File saving is not available in this preview.');
      return;
    }
    const dest = (targetDestination ?? destination).trim();
    if (!dest) {
      setErrorReason('No save destination was given. Enter a folder or file name, then retry.');
      setExportStatus('error');
      return;
    }
    setDestination(dest);
    setExportKind(kind);
    setExportStatus('busy');
    setErrorReason('');
    setNotice('');
    const payload = {
      kind,
      runId,
      destination: dest,
      report: {
        runName: run ? run.name : '',
        target: run ? run.target : '',
        state: run ? run.state : '',
        findings: runFindings.length,
        fails,
        passes,
      },
    };
    let result;
    try {
      result = await onExport(payload);
    } catch (err) {
      setErrorReason((err && err.message) || String(err) || 'The export failed.');
      setExportStatus('error');
      return;
    }
    if (result && result.ok && result.fileName) {
      setLastWrittenFile(result.fileName || '');
      if (result.destination) setDestination(result.destination);
      setExportStatus('success');
      return;
    }
    setErrorReason((result && result.error) || 'The file could not be written to that destination.');
    setExportStatus('error');
  };

  const onGenerateReport = () => startExport('report');
  const onExportLogs = () => startExport('logs');
  const onRetry = () => startExport(exportKind || 'report');
  const onChangeDestination = () => {
    setExportStatus('idle');
    setErrorReason('');
    setNotice('Enter a different save destination, then retry the export.');
    if (destinationRef.current) destinationRef.current.focus();
  };

  let statusPanel;
  if (exportStatus === 'busy') {
    statusPanel = (
      <div className="rec-status rec-status--busy" role="status" aria-live="polite">
        <div>
          <p className="rec-status__kick">Export in progress</p>
          <p className="rec-status__msg">
            {exportKind === 'logs' ? 'Writing the run log file…' : 'Generating the report file…'}
          </p>
          <p className="rec-status__file">Waiting for the save to finish.</p>
        </div>
      </div>
    );
  } else if (exportStatus === 'success') {
    statusPanel = (
      <div className="rec-status rec-status--success" role="status" aria-live="polite">
        <div>
          <p className="rec-status__kick">Saved</p>
          <p className="rec-status__msg">{exportKind === 'logs' ? 'Log file written.' : 'Report written.'}</p>
          <p className="rec-status__file">
            <strong>{lastWrittenFile}</strong> → {destination}
          </p>
        </div>
        <div className="rec-status__recovery">
          <button
            type="button"
            className="rec-link-btn"
            onClick={() => { setExportStatus('idle'); setNotice(''); }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  } else if (exportStatus === 'error') {
    statusPanel = (
      <div className="rec-status rec-status--error" role="alert">
        <div>
          <p className="rec-status__kick">Export failed</p>
          <p className="rec-status__msg">{errorReason}</p>
          <p className="rec-status__file">Destination: {destination || 'not set'}</p>
        </div>
        <div className="rec-status__recovery">
          <button type="button" className="rec-btn rec-btn--primary" onClick={onRetry}>Retry export</button>
          <button type="button" className="rec-btn rec-btn--secondary" onClick={onChangeDestination}>
            Choose a different destination
          </button>
        </div>
      </div>
    );
  } else {
    statusPanel = (
      <div className="rec-status rec-status--idle" role="status" aria-live="polite">
        <div>
          <p className="rec-status__kick">Ready</p>
          <p className="rec-status__msg">
            {notice || 'Choose a save destination, then generate the report or export the logs.'}
          </p>
          {onExport ? null : (
            <p className="rec-status__file">File saving is not available in this preview.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <section className="rec-root" aria-labelledby="rec-title">
      <div className="rec-inner" data-content-rail="workspace">
        <header className="rec-head">
          <div>
            <span className="rec-head__label">Export</span>
            <h2 id="rec-title" className="rec-head__title">Take the run with you</h2>
          </div>
          <p className="rec-head__run">
            {run ? `${run.name} · ${run.target}` : 'Run details unavailable'}
          </p>
        </header>

        <div className="rec-split">
          {/* ResultsReportPreview */}
          <article className="rec-preview" aria-label="Report preview">
            <div className="rec-preview__top">
              <p className="rec-preview__eyebrow">Report preview</p>
              <span className="rec-preview__state" data-state={run && run.state ? run.state : 'unknown'}>
                {stateLabel(run ? run.state : '')}
              </span>
            </div>
            {loading ? (
              <p className="rec-preview__name" aria-live="polite">Loading run records…</p>
            ) : loadError ? (
              <p className="rec-preview__name">Run records could not be loaded.</p>
            ) : (
              <>
                <h3 className="rec-preview__name">{run ? run.name : 'This run is not in the local history.'}</h3>
                {run ? <p className="rec-preview__target">{run.target}</p> : null}
                <dl className="rec-preview__counts">
                  <div className="rec-preview__count">
                    <dt>Findings</dt>
                    <dd>{total}</dd>
                  </div>
                  <div className="rec-preview__count" data-outcome="fail">
                    <dt>Fail</dt>
                    <dd>{fails}</dd>
                  </div>
                  <div className="rec-preview__count" data-outcome="pass">
                    <dt>Pass</dt>
                    <dd>{passes}</dd>
                  </div>
                </dl>
                <p className="rec-preview__foot">
                  {total === 0
                    ? 'No findings recorded for this run.'
                    : `Last finding ${stamp(lastTimestamp)} · ${total} of ${total} canonical findings`}
                </p>
              </>
            )}
          </article>

          {/* ResultsExportActions */}
          <div className="rec-actions">
            <div className="rec-actions__dest">
              <label className="rec-label" htmlFor="rec-destination">Save destination</label>
              <div className="rec-actions__dest-row">
                <input
                  id="rec-destination"
                  ref={destinationRef}
                  className="rec-input"
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="C:\\Brute\\reports"
                  aria-describedby="rec-destination-hint"
                  readOnly={busy}
                />
              </div>
              <p id="rec-destination-hint" className="rec-actions__hint">Folder or file path on this machine.</p>
            </div>
            <button
              type="button"
              className="rec-btn rec-btn--primary"
              onClick={onGenerateReport}
              disabled={busy}
              aria-busy={busy && exportKind === 'report'}
            >
              {busy && exportKind === 'report' ? <span className="rec-btn__busy" aria-hidden="true" /> : null}
              Generate and export report
            </button>
            <button
              type="button"
              className="rec-btn rec-btn--secondary"
              onClick={onExportLogs}
              disabled={busy}
              aria-busy={busy && exportKind === 'logs'}
            >
              {busy && exportKind === 'logs' ? <span className="rec-btn__busy" aria-hidden="true" /> : null}
              Export logs
            </button>
            <p className="rec-actions__hint">Files are written to the destination you enter.</p>
          </div>
        </div>

        {/* ResultsExportStatus */}
        {statusPanel}
      </div>
    </section>
  );
}